import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keepPreviousData } from '@tanstack/react-query'
import {
  ChevronDown, ChevronRight, Info, ListTree, RotateCcw, Search, Send, ShieldCheck, Trash2, X,
} from 'lucide-react'
import {
  notiApi, type Delivery, type DeliveryStatus, type EventFilters, type EventSummary, type TriggerResponse, type TxnAudit,
} from '../lib/api-noti'
import { scenarios, SCENARIO_GROUPS, type Scenario } from '../lib/scenarios'
import { AuditFilterBar, type FbField } from '../components/AuditFilterBar'
import { DeliveryStatusPill } from '../components/DeliveryStatusPill'
import { CHANNEL_META } from './ChannelsPage'

const STATUS_ORDER: DeliveryStatus[] = ['sent', 'skipped', 'failed', 'queued', 'processing']

// Pending search form — committed into the query (and the client-side dest
// filter) only when the user presses ค้นหา, mirroring the Audit Log bar.
const EMPTY_DRAFT = { transactionId: '', source: '', workflow: '', dest: '', status: '', from: '', to: '' }

// Status guide shown in the help modal — kept next to STATUS_ORDER so the copy
// stays in step with the engine's delivery lifecycle (internal/worker + plan.go).
const DELIVERY_GUIDE: { status: DeliveryStatus; title: string; desc: string; cause: string }[] = [
  { status: 'sent', title: 'ส่งสำเร็จ', desc: 'ช่องทางปลายทางรับเรื่องและส่งออกสำเร็จ มีเวลาส่ง (sentAt) กำกับ', cause: 'ส่งจริงผ่านช่องทาง — email / SMS / push / in-app / webhook' },
  { status: 'queued', title: 'อยู่ในคิว', desc: 'แถวรอ worker หยิบไปส่ง ยังไม่ถูกประมวลผล', cause: 'เพิ่งสร้างจากการ trigger · หรือส่งพลาดแล้วถูกตั้งคิวเพื่อลองใหม่ (มีเวลานัด nextRetryAt)' },
  { status: 'processing', title: 'กำลังส่ง', desc: 'worker จับงานนี้แล้วและกำลังยิงเข้าช่องทาง — เป็นสถานะชั่วคราว', cause: 'อยู่ระหว่างส่ง · ถ้าค้างเกิน 2 นาที ระบบจะดึงกลับเข้าคิวให้อัตโนมัติ' },
  { status: 'failed', title: 'ล้มเหลว (หยุดส่งแล้ว)', desc: 'พยายามส่งแล้วไม่สำเร็จและหยุดส่งถาวร — นับเป็นความล้มเหลว', cause: 'ข้อผิดพลาดถาวร (เทมเพลตผิด · payload ไม่ถูกต้อง · ช่องทางยังไม่ตั้งค่า · param บังคับขาด) หรือลองใหม่ครบจำนวนครั้งสูงสุด (ค่าเริ่มต้น 5 ครั้ง)' },
  { status: 'skipped', title: 'ข้ามการส่ง', desc: 'จงใจไม่ส่ง — ไม่นับเป็นความล้มเหลว', cause: 'ผู้รับปิดรับช่องทางนี้ใน workflow · ช่องทางปลายทางถูกปิด/ไม่รู้จัก · หรือ push ที่ผู้รับยังไม่มี device token' },
]
const EVENT_GUIDE: { cls: string; label: string; title: string; desc: string }[] = [
  { cls: 'ok', label: 'processed', title: 'กระจายสำเร็จ', desc: 'รับ event และแตกเป็น delivery รายปลายทางเรียบร้อย ตารางจะแสดง chip สรุปของแต่ละ delivery' },
  { cls: 'degraded', label: 'discarded', title: 'ไม่กระจาย', desc: 'รับ event แต่ไม่สร้าง delivery เลย เพราะ workflow ถูกปิดใช้งานอยู่' },
  { cls: 'info', label: 'duplicate', title: 'ซ้ำ (กันส่งซ้ำ)', desc: 'transactionId นี้เคยส่งแล้ว ระบบกันการส่งซ้ำ (idempotency) จึงคืน event เดิมโดยไม่สร้างใหม่' },
]

export default function TransactionsPage() {
  const qc = useQueryClient()
  const [filters, setFilters] = useState<EventFilters>({ page: 1, limit: 25 })
  const [destFilter, setDestFilter] = useState('')      // applied (client-side delivery filter)
  const [draft, setDraft] = useState(EMPTY_DRAFT)        // pending form values, committed on ค้นหา
  const [statusHelp, setStatusHelp] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  const clients = useQuery({ queryKey: ['noti-clients'], queryFn: () => notiApi.listClients() })
  const channels = useQuery({ queryKey: ['noti-channels'], queryFn: () => notiApi.listChannels() })
  const workflows = useQuery({ queryKey: ['noti-workflows'], queryFn: () => notiApi.listWorkflows() })
  const events = useQuery({
    queryKey: ['noti-events', filters],
    queryFn: () => notiApi.listEvents(filters),
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  })

  // Destination filter spans both worlds: channel ids and client ids.
  const destOptions = useMemo(() => [
    ...(channels.data?.data ?? []).map((c) => ({ value: `channel:${c.id}`, label: `Channel · ${c.name}` })),
    ...(clients.data?.data ?? []).map((c) => ({ value: `client:${c.id}`, label: `Client · ${c.name}` })),
  ], [channels.data, clients.data])

  // Audit-style filter bar: mutate a draft, then commit it to the query on Search.
  const onField = (key: string, value: string) =>
    setDraft((d) => ({ ...d, [key === 'search' ? 'transactionId' : key]: value }))
  const applyFilters = () => {
    setFilters({
      page: 1, limit: 25,
      source: draft.source || undefined,
      workflow: draft.workflow || undefined,
      status: draft.status || undefined,
      transactionId: draft.transactionId.trim() || undefined,
      from: draft.from ? new Date(draft.from).toISOString() : undefined,
      to: draft.to ? new Date(draft.to).toISOString() : undefined,
    })
    setDestFilter(draft.dest)
  }
  const resetFilters = () => {
    setDraft(EMPTY_DRAFT)
    setFilters({ page: 1, limit: 25 })
    setDestFilter('')
  }

  const primary: FbField[] = [
    { key: 'source', label: 'Client ต้นทาง', value: draft.source, kind: 'select',
      options: [{ value: '', label: 'ทั้งหมด' }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.id }))] },
    { key: 'workflow', label: 'Workflow', value: draft.workflow, kind: 'select',
      options: [{ value: '', label: 'ทั้งหมด' }, ...(workflows.data?.data ?? []).map((w) => ({ value: w.key, label: w.key }))] },
    { key: 'dest', label: 'ปลายทาง', value: draft.dest, kind: 'select',
      options: [{ value: '', label: 'ทั้งหมด' }, ...destOptions] },
  ]
  const advanced: FbField[] = [
    { key: 'status', label: 'สถานะ event', value: draft.status, kind: 'select',
      options: [{ value: '', label: 'ทั้งหมด' }, { value: 'processed', label: 'processed' }, { value: 'discarded', label: 'discarded' }] },
    { key: 'from', label: 'ตั้งแต่', value: draft.from, kind: 'datetime' },
    { key: 'to', label: 'ถึง', value: draft.to, kind: 'datetime' },
  ]

  const remove = useMutation({
    mutationFn: (eventId: string) => notiApi.deleteEvent(eventId),
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-events'] }),
  })
  const resend = useMutation({
    mutationFn: (eventId: string) => notiApi.resendEvent(eventId),
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-events'] }),
  })

  const totalPages = Math.max(1, Math.ceil((events.data?.total ?? 0) / (filters.limit ?? 25)))

  return (
    <>
      <div className="page-head page-head--stack">
        <div className="page-head-text">
          <h2><ListTree size={20} /> Transactions</h2>
          <p className="page-desc muted">
            ร่องรอยธุรกรรมการแจ้งเตือนทั้งหมดที่ผ่าน engine — มุมมองราย event พร้อมสถานะการส่ง
            รายปลายทาง ค้นหา/กรองตาม client ต้นทางและปลายทาง ส่งซ้ำหรือลบรายการทดสอบได้
          </p>
        </div>
        <button className="btn-primary" onClick={() => setComposerOpen(true)}><Send size={15} /> ส่ง Event</button>
      </div>

      <div className="page-head" style={{ margin: '0 0 0.5rem' }}>
        <span className="muted small">ตั้งเงื่อนไขแล้วกด “ค้นหา” · คลิก ▸ หน้าแถวเพื่อดูผลการส่งรายปลายทางและ audit log</span>
        <button className="btn-ghost" onClick={() => setStatusHelp(true)}><Info size={14} /> คำอธิบายสถานะ</button>
      </div>

      <AuditFilterBar
        search={draft.transactionId}
        searchPlaceholder="Transaction ID / เลขอ้างอิงจากระบบต้นทาง"
        primary={primary}
        advanced={advanced}
        loading={events.isFetching && events.isPlaceholderData}
        onField={onField}
        onSearch={applyFilters}
        onReset={resetFilters}
      />

      {events.isError && <div className="panel error">{(events.error as Error).message}</div>}

      <div className="panel">
        <table className="data-table events-table">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <th>เวลา</th><th>Workflow</th><th>ต้นทาง</th><th>Transaction ID</th>
              <th>ผู้รับ</th>
              <th>
                <span className="th-with-info">ผลการส่ง
                  <button className="th-info" title="คำอธิบายสถานะ" onClick={() => setStatusHelp(true)}><Info size={13} /></button>
                </span>
              </th>
              <th style={{ width: 130 }} />
            </tr>
          </thead>
          <tbody>
            {(events.data?.data ?? []).map((e) => (
              <Fragment key={e.eventId}>
                <tr className={expanded === e.eventId ? 'row-open' : ''}>
                  <td>
                    <button className="icon-ghost" onClick={() => setExpanded(expanded === e.eventId ? null : e.eventId)}>
                      {expanded === e.eventId ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </button>
                  </td>
                  <td className="muted small">{new Date(e.createdAt).toLocaleString('th-TH')}</td>
                  <td><code>{e.workflowKey}</code></td>
                  <td><span className="pill">{e.source}</span></td>
                  <td className="muted small"><code>{e.transactionId}</code></td>
                  <td>{e.recipientCount}</td>
                  <td><StatusChips e={e} /></td>
                  <td className="row-actions">
                    <button className="icon-ghost" title="ส่งซ้ำ (transactionId ใหม่)" disabled={resend.isPending}
                      onClick={() => { if (confirm('ส่งซ้ำด้วย payload/ผู้รับชุดเดิม?')) resend.mutate(e.eventId) }}>
                      <RotateCcw size={15} />
                    </button>
                    <button className="icon-ghost danger" title="ลบธุรกรรม (เก็บร่องรอยใน audit)" disabled={remove.isPending}
                      onClick={() => { if (confirm('ลบ event นี้พร้อม deliveries/แจ้งเตือนทั้งหมด? (audit log คงอยู่)')) remove.mutate(e.eventId) }}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
                {expanded === e.eventId && (
                  <tr className="expand-row">
                    <td colSpan={8}><EventDeliveries eventId={e.eventId} destFilter={destFilter} /></td>
                  </tr>
                )}
              </Fragment>
            ))}
            {events.data && events.data.data.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ textAlign: 'center' }}>ไม่พบธุรกรรมตามเงื่อนไข</td></tr>
            )}
          </tbody>
        </table>

        <div className="pager">
          <span className="muted small">ทั้งหมด {events.data?.total ?? 0} รายการ</span>
          <span className="pager-controls">
            <button className="btn-ghost" disabled={(filters.page ?? 1) <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}>ก่อนหน้า</button>
            <span>{filters.page} / {totalPages}</span>
            <button className="btn-ghost" disabled={(filters.page ?? 1) >= totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}>ถัดไป</button>
          </span>
        </div>
      </div>

      {statusHelp && <StatusGuideModal onClose={() => setStatusHelp(false)} />}
      {composerOpen && <ComposerModal onClose={() => setComposerOpen(false)} onFired={() => qc.invalidateQueries({ queryKey: ['noti-events'] })} />}
    </>
  )
}

// StatusGuideModal — a polished reference for every status surfaced in the
// Transactions table: the per-delivery lifecycle (the chips in ผลการส่ง) and
// the event-level outcome. Renders the real pills so the legend matches 1:1.
function StatusGuideModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal status-guide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3><Info size={17} /> คำอธิบายสถานะการส่ง</h3>
          <button className="icon-ghost" onClick={onClose} aria-label="ปิด"><X size={16} /></button>
        </div>

        <p className="sg-intro muted small">
          แต่ละ event จะแตกออกเป็น “การส่งรายปลายทาง” (delivery) หลายรายการ คอลัมน์ <b>ผลการส่ง</b> จึงสรุปเป็นจำนวนต่อสถานะ —
          เช่น <span className="chip-count sent">sent 1</span> <span className="chip-count skipped">skipped 1</span> หมายถึงส่งสำเร็จ 1 และข้าม 1 รายการ
        </p>

        <div className="sg-section-title">สถานะการส่งรายปลายทาง (Delivery)</div>
        <div className="sg-list">
          {DELIVERY_GUIDE.map((s) => (
            <div key={s.status} className={`sg-item sg-${s.status}`}>
              <div className="sg-pill"><DeliveryStatusPill status={s.status} /></div>
              <div className="sg-body">
                <div className="sg-name">{s.title}</div>
                <div className="sg-desc">{s.desc}</div>
                <div className="sg-cause"><b>เกิดจาก:</b> {s.cause}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="sg-section-title">สถานะของทั้งรายการ (Event)</div>
        <div className="sg-list">
          {EVENT_GUIDE.map((s) => (
            <div key={s.label} className="sg-item">
              <div className="sg-pill"><span className={`pill ${s.cls}`}>{s.label}</span></div>
              <div className="sg-body">
                <div className="sg-name">{s.title}</div>
                <div className="sg-desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="sg-note">
          <b>ข้อสังเกต</b> — <span className="chip-count skipped">skipped</span> คือ “จงใจไม่ส่ง” ไม่นับเป็นความล้มเหลว ต่างจาก
          {' '}<span className="chip-count failed">failed</span> ที่พยายามส่งแล้วไม่สำเร็จจริง · เมื่อส่งพลาดชั่วคราว ระบบจะลองใหม่อัตโนมัติแบบถอยเวลา
          {' '}30 วินาที → 2 นาที → 10 นาที → 30 นาที จนครบจำนวนครั้งสูงสุดจึงกลายเป็น failed
        </div>

        <div className="modal-actions">
          <button className="btn-primary" onClick={onClose}>เข้าใจแล้ว</button>
        </div>
      </div>
    </div>
  )
}

function StatusChips({ e }: { e: EventSummary }) {
  if (e.status === 'discarded') return <span className="pill degraded">discarded</span>
  return (
    <span className="status-chips">
      {STATUS_ORDER.filter((s) => (e.statusCounts[s] ?? 0) > 0).map((s) => (
        <span key={s} className={`chip-count ${s}`}>{s} {e.statusCounts[s]}</span>
      ))}
    </span>
  )
}

// Expanded row: deliveries of one event (with optional destination filter).
// Each delivery renders as a card; a failed / skipped / retrying one surfaces
// the FULL engine error verbatim plus a best-effort cause→fix hint, so the
// calling system (LMS, LOS, …) can see exactly what went wrong and what to fix.
function EventDeliveries({ eventId, destFilter }: { eventId: string; destFilter: string }) {
  const detail = useQuery({ queryKey: ['noti-event', eventId], queryFn: () => notiApi.getEvent(eventId) })
  if (detail.isLoading) return <p className="muted small" style={{ margin: '0.4rem 0' }}>กำลังโหลด…</p>
  if (detail.isError) return <div className="error">{(detail.error as Error).message}</div>

  const [kind, ref] = destFilter ? destFilter.split(':') : ['', '']
  const rows = (detail.data?.deliveries ?? []).filter((d) =>
    !destFilter || (d.destinationKind === kind && d.destinationRef === ref))

  return (
    <div className="expand-body">
      <TransactionAudit trail={detail.data?.audit ?? []} />
      <div className="expand-section">
        <div className="expand-section-title">ผลการส่งรายปลายทาง ({rows.length})</div>
        {rows.length === 0
          ? <p className="muted small" style={{ margin: '0.2rem 0' }}>ไม่มี delivery ตามตัวกรองปลายทาง</p>
          : <div className="delivery-list">{rows.map((d) => <DeliveryCard key={d.id} d={d} />)}</div>}
      </div>
    </div>
  )
}

// Action codes → Thai label for the audit display.
const AUDIT_ACTION_LABEL: Record<string, string> = {
  'notification.send': 'ส่งการแจ้งเตือน (Send Notification)',
  'notification.resend': 'ส่งซ้ำ (Resend)',
}

// TransactionAudit renders the per-transaction audit trail (TOR ข้อ 9.2) — one
// block per Send/Resend, each laying out all 13 required fields in the spec's
// order. Empty for events created before this feature shipped.
function TransactionAudit({ trail }: { trail: TxnAudit[] }) {
  return (
    <div className="expand-section">
      <div className="expand-section-title"><ShieldCheck size={14} /> Audit Log — บันทึกการตรวจสอบ (TOR ข้อ 9.2)</div>
      {trail.length === 0 ? (
        <p className="muted small" style={{ margin: '0.2rem 0' }}>
          ยังไม่มีบันทึก audit สำหรับธุรกรรมนี้ (เป็นรายการก่อนเปิดใช้งานฟีเจอร์นี้)
        </p>
      ) : (
        <div className="txn-audit">{trail.map((a) => <AuditEntry key={a.id} a={a} />)}</div>
      )}
    </div>
  )
}

function AuditEntry({ a }: { a: TxnAudit }) {
  const ok = a.status === 'success'
  const statusPill = <span className={`pill ${ok ? 'ok' : 'down'}`}>{ok ? 'Success' : 'Failed'}</span>
  // The 13 TOR fields, in spec order (ข้อ 9.2). Always render every row — '—'
  // for fields that legitimately don't apply (system-triggered sends have no
  // employee/approver) so the auditor sees a complete, honest record.
  const fields: { code: string; sub: string; val: ReactNode }[] = [
    { code: 'Timestamp', sub: 'วันเวลา', val: new Date(a.timestamp).toLocaleString('th-TH') },
    { code: 'User_id', sub: 'คนที่ทำรายการ', val: a.userId },
    { code: 'Employee_id', sub: 'รหัสพนักงาน', val: a.employeeId },
    { code: 'Role', sub: 'Role ตอนทำรายการ', val: a.role },
    { code: 'Action', sub: 'ทำอะไร', val: AUDIT_ACTION_LABEL[a.action] ?? a.action },
    { code: 'Module', sub: 'โมดูลในระบบ', val: a.module },
    { code: 'Target_object', sub: 'สิ่งที่ถูกกระทำ (ผู้รับ)', val: a.targetObject },
    { code: 'Before_value', sub: 'ก่อนเปลี่ยน', val: auditVal(a.beforeValue) },
    { code: 'After_value', sub: 'หลังเปลี่ยน', val: auditVal(a.afterValue) },
    { code: 'Status', sub: 'Success / Failed', val: statusPill },
    { code: 'Approved_by', sub: 'คนอนุมัติ', val: a.approvedBy },
    { code: 'Ip_address', sub: 'IP', val: a.ipAddress },
    { code: 'Device', sub: 'ระบบ/อุปกรณ์', val: a.device },
  ]
  return (
    <div className="ta-entry">
      <div className="ta-head">
        {statusPill}
        <b>{AUDIT_ACTION_LABEL[a.action] ?? a.action}</b>
        <span className="muted small ta-when">{new Date(a.timestamp).toLocaleString('th-TH')}</span>
      </div>
      <div className="ta-grid">
        {fields.map((f) => (
          <div className="ta-row" key={f.code}>
            <span className="ta-label"><code>{f.code}</code> <span className="muted">{f.sub}</span></span>
            <span className="ta-val">{emptyDash(f.val)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// auditVal renders a stored before/after JSON snapshot compactly; primitives
// pass through as-is. Empty values fall through to emptyDash → '—'.
function auditVal(v: unknown): ReactNode {
  if (v === null || v === undefined || v === '') return ''
  if (typeof v === 'object') return <code className="ta-json">{JSON.stringify(v)}</code>
  return String(v)
}

function emptyDash(v: ReactNode): ReactNode {
  return v === null || v === undefined || v === '' ? <span className="muted">—</span> : v
}

// One delivery: a header line, plus a detailed failure block whenever it didn't
// send. queued+lastError is a retry-in-progress and shows its reason too.
function DeliveryCard({ d }: { d: Delivery }) {
  const hasError = !!d.lastError && d.status !== 'sent'
  const hint = hasError ? explainError(d.lastError as string) : null
  const when = d.sentAt ?? d.updatedAt
  const tone = d.status === 'failed' ? 'is-failed' : d.status === 'skipped' ? 'is-warn' : hasError ? 'is-warn' : ''
  return (
    <div className={`delivery-card ${tone}`}>
      <div className="delivery-head">
        <DeliveryStatusPill status={d.status} />
        <code className="dc-channel">{d.channel}</code>
        <span className="muted small">
          ปลายทาง: {d.destinationRef
            ? <>{d.destinationKind === 'client' ? 'app' : 'channel'} · <code>{d.destinationRef}</code></>
            : '—'}
        </span>
        <span className="muted small">ผู้รับ: <code>{d.subscriberId}</code></span>
        <span className="muted small dc-when">{d.sentAt ? 'ส่งเมื่อ ' : 'อัปเดต '}{new Date(when).toLocaleString('th-TH')}</span>
      </div>

      {hasError && (
        <div className="delivery-error">
          <div className="de-label">สาเหตุที่ส่งไม่สำเร็จ ({d.status})</div>
          <pre className="de-raw">{d.lastError}</pre>
          {hint && (
            <div className="de-hint">
              <div><b>น่าจะเกิดจาก:</b> {hint.cause}</div>
              <div><b>วิธีแก้:</b> {hint.fix}</div>
            </div>
          )}
          <div className="de-meta muted small">
            พยายามส่งแล้ว {d.attempts} ครั้ง
            {d.status === 'queued' && d.nextRetryAt
              ? ` · จะลองใหม่ ${new Date(d.nextRetryAt).toLocaleString('th-TH')}`
              : d.status === 'failed' ? ' · หยุดส่งแล้ว (เกินจำนวนครั้ง หรือเป็นข้อผิดพลาดถาวร)'
              : d.status === 'skipped' ? ' · ข้ามการส่ง (ไม่นับเป็นความล้มเหลว)' : ''}
          </div>
        </div>
      )}

      {!hasError && d.renderedSubject && (
        <div className="delivery-subject muted small">หัวข้อที่ส่ง: {d.renderedSubject}</div>
      )}
    </div>
  )
}

// explainError maps the engine's well-known error prefixes to a Thai cause/fix.
// Conservative: returns null for anything unrecognised so the raw text still
// stands alone. Patterns mirror internal/worker/worker.go + the channel senders.
function explainError(raw: string): { cause: string; fix: string } | null {
  const e = raw.toLowerCase()
  if (e.includes('template render') || e.includes('map has no entry for key') || e.includes('executing "t"'))
    return {
      cause: 'เทมเพลตอ้างถึง field ที่ payload ไม่มี (หรือชนิดข้อมูลไม่ตรง) ตอน render',
      fix: 'ส่ง payload ให้มี key ที่เทมเพลตเรียกใช้ครบ และชนิดถูกต้องตาม payloadSchema ของ workflow — ดูชื่อ key ที่ขาดได้จากข้อความ error',
    }
  if (e.includes('attachment fetch'))
    return {
      cause: 'ดาวน์โหลดไฟล์แนบจาก URL ใน payload ไม่สำเร็จ (เข้าไม่ถึง / HTTP ไม่ใช่ 200)',
      fix: 'ตรวจ URL ไฟล์แนบให้เซิร์ฟเวอร์เข้าถึงได้จริงและตอบ 200 (เลี่ยง URL ตัวอย่าง เช่น example.com)',
    }
  if (e.includes('missing required keys') || e.includes('param_map missing'))
    return {
      cause: 'destination param_map ส่ง param ที่ช่องทางบังคับมาไม่ครบ',
      fix: 'เพิ่ม param ที่ขาด (เช่น subject/body/title) ใน param_map ของ destination หรือใน payload',
    }
  if (e.includes('no template for channel'))
    return {
      cause: 'workflow นี้ยังไม่มีเทมเพลตของช่องทาง/locale ที่ใช้ส่ง',
      fix: 'สร้างเทมเพลตของช่องทางนี้ในหน้า Workflows',
    }
  if (e.includes('channel not configured'))
    return {
      cause: 'ยังไม่ได้ตั้งค่า sender ของช่องทางนี้ในระบบ',
      fix: 'ตั้งค่า channel ในหน้า Channels (หรือ env) ให้พร้อมใช้งาน',
    }
  if (e.includes('channel disabled'))
    return {
      cause: 'channel ปลายทางถูกปิดใช้งานหลังจากตั้งคิวส่งแล้ว',
      fix: 'เปิดใช้งาน channel ในหน้า Channels แล้วกดส่งซ้ำ',
    }
  if (e.includes('payload unmarshal'))
    return {
      cause: 'payload ที่ส่งมาไม่ใช่ JSON object ที่ถูกต้อง',
      fix: 'ส่ง payload เป็น JSON ที่ valid',
    }
  if (e.includes('401') || e.includes('token exchange') || e.includes('credential'))
    return {
      cause: 'ยืนยันตัวตนกับผู้ให้บริการช่องทางไม่ผ่าน (เช่น credentials ของ FCM/SMTP)',
      fix: 'ตรวจ service account / app password / API key ของ channel ให้ถูกต้องและยังไม่หมดอายุ',
    }
  if (e.includes('device token') || e.includes('no token'))
    return {
      cause: 'ผู้รับยังไม่มี device token ลงทะเบียนสำหรับ push',
      fix: 'ให้แอปฝั่งผู้รับลงทะเบียน FCM token ก่อน แล้วจึงกดส่งซ้ำ',
    }
  return null
}

// Composer: fire a new event from presets or hand-written JSON.
function ComposerModal({ onClose, onFired }: { onClose: () => void; onFired: () => void }) {
  const [scenario, setScenario] = useState<Scenario | null>(null)
  const [body, setBody] = useState('{\n  "name": "system.mock.smoke",\n  "to": [{"subscriberId": "CIF-6900001"}],\n  "payload": {"note": "ทดสอบจากหน้า Transactions"}\n}')
  const [result, setResult] = useState<TriggerResponse | null>(null)

  const fire = useMutation({
    mutationFn: () => {
      const parsed = JSON.parse(body)
      parsed.transactionId = `PORTAL-${Date.now()}`
      return notiApi.trigger(parsed)
    },
    onSuccess: (r) => {
      setResult(r)
      onFired()
    },
  })

  const pick = (s: Scenario) => {
    setScenario(s)
    setResult(null)
    setBody(JSON.stringify(s.request, null, 2))
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3><Send size={17} /> ส่ง Event เข้า Notification Center</h3>
          <button className="icon-ghost" onClick={onClose} aria-label="ปิด"><X size={16} /></button>
        </div>
        <div className="composer-grid">
          <div className="composer-rail">
            {(Object.keys(SCENARIO_GROUPS) as Scenario['group'][]).map((g) => (
              <div key={g}>
                <div className="rail-group">{SCENARIO_GROUPS[g]}</div>
                {scenarios.filter((s) => s.group === g).map((s) => (
                  <button key={s.id} className={`scenario-btn ${scenario?.id === s.id ? 'active' : ''}`} onClick={() => pick(s)}>
                    {s.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="composer-main">
            <p className="muted small" style={{ margin: '0 0 0.4rem' }}>
              เลือก scenario จากซ้ายหรือแก้ JSON เอง — ระบบใส่ <code>transactionId</code> ใหม่ให้อัตโนมัติ
            </p>
            <textarea rows={14} value={body} spellCheck={false} onChange={(e) => setBody(e.target.value)} />
            {fire.isError && <div className="error">{(fire.error as Error).message}</div>}
            {result && (
              <div className="test-result ok">
                <Search size={15} />
                <div className="small">
                  รับแล้ว — status <b>{result.status}</b> · deliveries {result.deliveries} ·
                  eventId <code>{result.eventId.slice(0, 13)}…</code> (ดูผลในตาราง)
                </div>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-ghost" onClick={onClose}>ปิด</button>
              <button className="btn-primary" disabled={fire.isPending} onClick={() => fire.mutate()}>
                <Send size={14} /> {fire.isPending ? 'กำลังส่ง…' : 'ยิง Trigger'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Re-exported for the channel meta labels in destination columns.
void CHANNEL_META
